import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  VStack,
  HStack,
  Text,
  Input,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Box,
  SimpleGrid,
  RadioGroup,
  Radio,
  Icon,
  useToast,
  useBreakpointValue,
  Badge,
  Divider,
  Checkbox,
  Select,
} from '@chakra-ui/react';
import { Controller, useForm } from 'react-hook-form';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { FaCreditCard, FaApple, FaGoogle, FaShieldAlt, FaLock } from 'react-icons/fa';
import { useWalletContext } from '../../context/WalletContext';
import { useSubscription } from '../../context/SubscriptionContext';
import PaymentRequestButton from '../Payments/PaymentRequestButton';
import CustomSelect from '../../utils/CustomSelect';
import { getErrorMessage } from '../../utils/errorUtils';

const PaymentMethodSelection = ({ 
  selectedPaymentMethod, 
  setSelectedPaymentMethod, 
  applePaySupported, 
  googlePaySupported, 
  supportCheckLoading 
}) => {
  return (
    <FormControl>
      <FormLabel fontSize="md" fontWeight="bold" color="secondary.100" mb={4}>
        Payment Method
      </FormLabel>
      <Box bg="primary.100" p={4} borderRadius="lg" border="1px solid" borderColor="dark.900">
        <RadioGroup onChange={setSelectedPaymentMethod} value={selectedPaymentMethod}>
          <VStack spacing={3} align="stretch">
            <Radio value="card" size="lg" colorScheme="orange">
              <HStack>
                <Icon as={FaCreditCard} boxSize={5} color="secondary.500" />
                <Text color="dark.500">Credit/Debit Card</Text>
              </HStack>
            </Radio>
            
            <Radio 
              value="applePay" 
              isDisabled={!applePaySupported && !supportCheckLoading}
              size="lg"
              colorScheme="orange"
            >
              <HStack>
                <Icon 
                  as={FaApple} 
                  boxSize={5} 
                  color={!applePaySupported && !supportCheckLoading ? "dark.900" : "secondary.500"} 
                />
                <Text color={!applePaySupported && !supportCheckLoading ? "dark.500" : "secondary.500"}>
                  Apple Pay
                </Text>
                {!applePaySupported && !supportCheckLoading && (
                  <Badge colorScheme="red" size="sm">Not Available</Badge>
                )}
              </HStack>
            </Radio>
            
            <Radio 
              value="googlePay" 
              isDisabled={!googlePaySupported && !supportCheckLoading}
              size="lg"
              colorScheme="orange"
            >
              <HStack>
                <Icon 
                  as={FaGoogle} 
                  boxSize={5} 
                  color={!googlePaySupported && !supportCheckLoading ? "dark.500" : "secondary.500"} 
                />
                <Text color={!googlePaySupported && !supportCheckLoading ? "dark.500" : "secondary.500"}>
                  Google Pay
                </Text>
                {!googlePaySupported && !supportCheckLoading && (
                  <Badge colorScheme="red" size="sm">Not Available</Badge>
                )}
              </HStack>
            </Radio>
          </VStack>
        </RadioGroup>
      </Box>
    </FormControl>
  );
};


const AddFundsModal = ({ isOpen, onClose, onSuccess, minimumAmount = 0, suggestedAmount = 50 }) => {
  const { register, handleSubmit, formState: { errors }, control, watch, setValue } = useForm();
  const toast = useToast();
  const stripe = useStripe();
  const elements = useElements();
  const { addFunds } = useWalletContext();
  const { paymentMethods, getPaymentMethods } = useSubscription();
  
  // Responsive values
  const modalSize = useBreakpointValue({ base: 'full', md: 'xl' });
  
  // State
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('card');
  const [savedPaymentMethod, setSavedPaymentMethod] = useState('');
  const [loading, setLoading] = useState(false);
  const [processingFees, setProcessingFees] = useState({ fee: 0, net: 0 });
  const [savePaymentMethod, setSavePaymentMethod] = useState(true);
  
  // Payment Request support
  const [applePaySupported, setApplePaySupported] = useState(false);
  const [googlePaySupported, setGooglePaySupported] = useState(false);
  const [supportCheckLoading, setSupportCheckLoading] = useState(false);
  
  // Form values
  const depositAmountRaw = watch('amount', suggestedAmount);
  const depositAmount = parseFloat(depositAmountRaw) || 0;
  
  // Quick amount options
  const quickAmounts = [
    Math.max(25, Math.ceil(minimumAmount)),
    Math.max(50, Math.ceil(minimumAmount / 50) * 50),
    Math.max(100, Math.ceil(minimumAmount / 100) * 100),
    Math.max(250, Math.ceil(minimumAmount / 250) * 250)
  ].filter((amount, index, arr) => arr.indexOf(amount) === index);

  // Payment method support check
  const checkPaymentMethodSupport = useCallback(async () => {
    if (!stripe || !isOpen) return;

    setSupportCheckLoading(true);
    try {
      const paymentRequest = stripe.paymentRequest({
        country: 'US',
        currency: 'usd',
        total: {
          label: 'Test',
          amount: 100,
        },
        requestPayerName: true,
        requestPayerEmail: true,
      });

      const canMakePayment = await paymentRequest.canMakePayment();
      if (canMakePayment) {
        setApplePaySupported(canMakePayment.applePay || false);
        setGooglePaySupported(canMakePayment.googlePay || false);
      } else {
        setApplePaySupported(false);
        setGooglePaySupported(false);
      }
    } catch (error) {
      console.error('Error checking payment method support:', error);
      setApplePaySupported(false);
      setGooglePaySupported(false);
    } finally {
      setSupportCheckLoading(false);
    }
  }, [stripe, isOpen]);

  // Calculate processing fees (Stripe fee ~2.9% + 30¢ + $5 fixed fee)
  // User pays: desired amount + fees
  useEffect(() => {
    if (depositAmount > 0) {
      const stripeFee = depositAmount * 0.029 + 0.30; // Stripe's fee
      const fixedFee = 5.00; // Fixed processing fee
      const totalFee = stripeFee + fixedFee;
      const totalToPay = depositAmount + totalFee;
      setProcessingFees({ 
        fee: totalFee, 
        net: depositAmount, // This is what they get in wallet
        totalToPay: totalToPay 
      });
    } else {
      setProcessingFees({ fee: 0, net: 0, totalToPay: 0 });
    }
  }, [depositAmount]);

  // Load data when modal opens
  useEffect(() => {
    if (isOpen) {
      checkPaymentMethodSupport();
      getPaymentMethods();
      setValue('amount', suggestedAmount);
    }
  }, [isOpen, checkPaymentMethodSupport, getPaymentMethods, setValue, suggestedAmount]);

  const handleQuickAmount = (amount) => {
    setValue('amount', amount);
  };

  const handleAddFunds = async (data) => {
    if (!stripe || !elements) {
      toast({
        title: 'Error',
        description: 'Payment system not loaded. Please refresh and try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    try {
      let paymentMethodId = null;

      if (selectedPaymentMethod === 'saved' && savedPaymentMethod) {
        paymentMethodId = savedPaymentMethod;
      } else if (selectedPaymentMethod === 'card') {
        const cardElement = elements.getElement(CardElement);
        if (!cardElement) {
          throw new Error('Please enter valid card details');
        }

        const { error, paymentMethod } = await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement,
          billing_details: {
            name: data.cardName,
            address: {
              line1: data.billingAddress,
              city: data.city,
              state: data.state,
              postal_code: data.postalCode,
              country: data.country,
            },
          },
        });

        if (error) {
          throw new Error(error.message);
        }

        paymentMethodId = paymentMethod.id;
      } else if (selectedPaymentMethod === 'applePay' || selectedPaymentMethod === 'googlePay') {
        toast({
          title: 'Use Payment Button',
          description: 'Please use the Apple Pay or Google Pay button below.',
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      if (!paymentMethodId) {
        throw new Error('Please select a payment method');
      }

      const result = await addFunds({
        amount: parseFloat(data.amount),
        payment_method_id: paymentMethodId,
      });

      if (result.success) {
        toast({
          title: 'Funds added successfully',
          description: `$${result.amount_deposited.toFixed(2)} has been added to your wallet.`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        onSuccess();
      }
    } catch (error) {
      // Check if it's a network error that might indicate payment succeeded but response failed
      const isNetworkError = error.message?.includes('Network Error') || 
                            error.message?.includes('No response returned');
      
      if (isNetworkError) {
        toast({
          title: 'Connection Issue',
          description: 'Payment may have been processed. Please check your wallet balance and contact support if needed.',
          status: 'warning',
          duration: 8000,
          isClosable: true,
        });
        // Still call onSuccess to refresh wallet and close modal
        onSuccess();
      } else {
        toast({
          title: 'Payment Failed',
          description: getErrorMessage(error, 'Failed to add funds. Please try again.'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentRequestSuccess = () => {
    toast({
      title: 'Funds added successfully',
      description: `Funds have been added to your wallet via ${selectedPaymentMethod}.`,
      status: 'success',
      duration: 5000,
      isClosable: true,
    });
    onSuccess();
  };

  const COUNTRIES = [
    { name: "United States", code: "US" },
    { name: "Canada", code: "CA" },
    { name: "United Kingdom", code: "GB" },
    { name: "Australia", code: "AU" },
    { name: "Germany", code: "DE" },
    { name: "France", code: "FR" },
  ];

  const CARD_OPTIONS = {
    style: {
      base: {
        fontSize: "16px",
        color: "#000000ff",
        fontFamily: "Inter, sans-serif",
        "::placeholder": { color: "#a0aec0" },
        backgroundColor: "transparent",
      },
      invalid: {
        color: "#ff4c4c",
        iconColor: "#ff4c4c",
      },
      complete: {
        color: "#4caf50",
      },
    },
    hidePostalCode: true,
    disableLink: true,
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={modalSize} closeOnOverlayClick={false}>
      <ModalOverlay backdropFilter="blur(4px)" bg="blackAlpha.600" />
      <ModalContent bg="dark.100" border="1px solid" borderColor="dark.900" color="primary.500" mx={{ base: 4, md: 0 }} borderRadius="xl">
        <ModalHeader borderBottom="1px solid" borderColor="dark.900" pb={4}>
          <HStack>
            <Box p={2} bg="green.500" borderRadius="lg">
              <Icon as={FaShieldAlt} color="white" boxSize={5} />
            </Box>
            <VStack align="start" spacing={0}>
              <Text fontSize="lg" fontWeight="bold" color="primary.100">Add Funds to Wallet</Text>
              <Text fontSize="sm" color="secondary.900">Secure payment powered by Stripe</Text>
            </VStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        
        <ModalBody py={6}>
          <form onSubmit={handleSubmit(handleAddFunds)}>
            <VStack spacing={6}>
              {/* Amount Selection */}
              <FormControl isInvalid={errors.amount}>
                <FormLabel color="secondary.100" fontWeight="bold">Amount to Add</FormLabel>
                <VStack spacing={4}>
                  <Controller
                    name="amount"
                    control={control}
                    rules={{
                      required: "Amount is required",
                      min: { value: Math.max(5, minimumAmount), message: `Minimum amount is $${Math.max(5, minimumAmount)}` },
                      max: { value: 10000, message: "Maximum amount is $10,000" }
                    }}
                    render={({ field }) => (
                      <NumberInput
                        {...field}
                        min={Math.max(5, minimumAmount)}
                        max={10000}
                        precision={2}
                        w="100%"
                        variant="light"
                        border="1px solid"
                        borderColor="dark.900"
                      >
                        <NumberInputField
                          placeholder={`$${Math.max(5, minimumAmount)}.00`}
                          _placeholder={{ color: "primary.900" }}
                        />
                        <NumberInputStepper>
                          <NumberIncrementStepper color="dark.500" borderColor="dark.900" />
                          <NumberDecrementStepper color="dark.500" borderColor="dark.900" />
                        </NumberInputStepper>
                      </NumberInput>
                    )}
                  />
                  
                  {/* Quick Amount Buttons */}
                  <SimpleGrid columns={4} spacing={2} w="100%">
                    {quickAmounts.map((amount) => (
                      <Button
                        key={amount}
                        size="sm"
                        onClick={() => handleQuickAmount(amount)}
                        variant={depositAmount === amount ? "solid" : "outline"}
                        bg={depositAmount === amount ? "secondary.500" : "transparent"}
                        color={depositAmount === amount ? "dark.900" : "secondary.500"}
                        borderColor="dark.900"
                        _hover={{
                          bg: "secondary.500",
                          color: "dark.900"
                        }}
                      >
                        ${amount}
                      </Button>
                    ))}
                  </SimpleGrid>
                </VStack>
                <FormErrorMessage>{errors.amount && errors.amount.message}</FormErrorMessage>
              </FormControl>

              {/* Fee Breakdown Display */}
              {depositAmount > 0 && (
                <Box bg="primary.100" p={4} w="100%" borderRadius="lg" border="1px solid" borderColor="dark.900">
                  <VStack align="start" spacing={2}>
                    <Text fontSize="sm" fontWeight="bold" color="primary.100">
                      Payment Breakdown
                    </Text>
                    <VStack align="stretch" spacing={1} w="100%">
                      <HStack justify="space-between">
                        <Text fontSize="sm" color="secondary.900">Amount to add to wallet:</Text>
                        <Text fontSize="sm" fontWeight="bold" color="dark.100">
                          ${depositAmount.toFixed(2)}
                        </Text>
                      </HStack>
                      <HStack justify="space-between">
                        <Text fontSize="sm" color="secondary.900">Stripe processing fee:</Text>
                        <Text fontSize="sm" color="dark.100">
                          ${((depositAmount * 0.029) + 0.30).toFixed(2)}
                        </Text>
                      </HStack>
                      <HStack justify="space-between">
                        <Text fontSize="sm" color="secondary.900">Platform fee:</Text>
                        <Text fontSize="sm" color="dark.100">
                          $5.00
                        </Text>
                      </HStack>
                      <Box borderTop="1px solid" borderColor="dark.900" pt={2}>
                        <HStack justify="space-between">
                          <Text fontSize="md" fontWeight="bold" color="dark.100">
                            Total to charge:
                          </Text>
                          <Text fontSize="md" fontWeight="bold" color="dark.500">
                            ${(processingFees.totalToPay || 0).toFixed(2)}
                          </Text>
                        </HStack>
                      </Box>
                    </VStack>
                  </VStack>
                </Box>
              )}


              {/* Payment Method Choice */}
              {paymentMethods && paymentMethods.length > 0 ? (
                <VStack spacing={4} align="stretch" w="100%">
                  <FormControl>
                    <FormLabel color="dark.500" fontWeight="bold">Payment Method</FormLabel>
                    <Box bg="primary.100" p={4} borderRadius="lg" border="1px solid" borderColor="dark.900">
                      <RadioGroup 
                        value={selectedPaymentMethod === 'saved' ? 'saved' : 'new'} 
                        onChange={(value) => {
                          if (value === 'saved') {
                            setSelectedPaymentMethod('saved');
                            // Set to first saved method if none selected
                            if (!savedPaymentMethod && paymentMethods.length > 0) {
                              setSavedPaymentMethod(paymentMethods[0].id);
                            }
                          } else {
                            setSelectedPaymentMethod('card');
                            setSavedPaymentMethod('');
                          }
                        }}
                      >
                        <VStack spacing={3} align="stretch">
                          <Radio value="saved" size="lg" colorScheme="orange">
                            <Text color="dark.500">Use saved payment method</Text>
                          </Radio>
                          <Radio value="new" size="lg" colorScheme="orange">
                            <Text color="dark.500">Use new payment method</Text>
                          </Radio>
                        </VStack>
                      </RadioGroup>
                    </Box>
                  </FormControl>
                  
                  {/* Saved Payment Method Selection */}
                  {selectedPaymentMethod === 'saved' && (
                    <FormControl>
                      <FormLabel color="secondary.100" fontWeight="bold">Select Saved Payment Method</FormLabel>
                      <Select
                        variant="light"
                        placeholder="Choose a payment method"
                        _placeholder={{ color: "primary.900" }}
                        value={savedPaymentMethod}
                        onChange={(e) => setSavedPaymentMethod(e.target.value)}
                      >
                        {paymentMethods.map((method) => (
                          <option key={method.id} value={method.id}>
                            {method.card?.brand.toUpperCase()} ****{method.card?.last4}
                            {method.card?.exp_month && method.card?.exp_year && (
                              ` - ${method.card.exp_month.toString().padStart(2, '0')}/${method.card.exp_year.toString().slice(-2)}`
                            )}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </VStack>
              ) : (
                <Box p={4} bg="primary.100" borderRadius="lg" border="1px solid" borderColor="dark.900">
                  <Text color="dark.500" fontSize="sm" textAlign="center">
                    No saved payment methods found. You can add a new payment method below.
                  </Text>
                </Box>
              )}

              {/* Payment Method Selection (only show if no saved method selected) */}
              {selectedPaymentMethod !== 'saved' && (
                <>
                  <PaymentMethodSelection
                    selectedPaymentMethod={selectedPaymentMethod}
                    setSelectedPaymentMethod={setSelectedPaymentMethod}
                    applePaySupported={applePaySupported}
                    googlePaySupported={googlePaySupported}
                    supportCheckLoading={supportCheckLoading}
                  />

                  {/* Apple Pay / Google Pay Button */}
                  {(selectedPaymentMethod === 'applePay' || selectedPaymentMethod === 'googlePay') && (
                    <Box w="100%">
                      <PaymentRequestButton
                        amount={Math.round((processingFees.totalToPay || 0) * 100)}
                        currency="usd"
                        country="US"
                        label={`Pay $${(processingFees.totalToPay || 0).toFixed(2)} - Add $${(depositAmount || 0).toFixed(2)} to Wallet`}
                        onPaymentSuccess={handlePaymentRequestSuccess}
                        onPaymentError={(error) => {
                          toast({
                            title: "Payment Error",
                            description: getErrorMessage(error, "Payment failed. Please try again."),
                            status: "error",
                            duration: 5000,
                            isClosable: true,
                          });
                        }}
                      />
                    </Box>
                  )}

                  {/* Card Details Form */}
                  {selectedPaymentMethod === 'card' && (
                    <VStack spacing={4} w="100%">
                      <FormControl isInvalid={errors.cardName}>
                        <FormLabel color="secondary.100">Name on Card</FormLabel>
                        <Input
                          {...register("cardName", { required: "Name on card is required" })}
                          placeholder="John Doe"
                          variant="light"
                          _placeholder={{ color: "primary.900" }}
                        />
                        <FormErrorMessage>{errors.cardName && errors.cardName.message}</FormErrorMessage>
                      </FormControl>

                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="100%">
                        <FormControl isInvalid={errors.billingAddress}>
                          <FormLabel color="secondary.100">Billing Address</FormLabel>
                          <Input
                            {...register('billingAddress', { required: 'Address is required' })}
                            placeholder="123 Main St"
                            variant="light"
                            _placeholder={{ color: "primary.900" }}
                          />
                          <FormErrorMessage>{errors.billingAddress && errors.billingAddress.message}</FormErrorMessage>
                        </FormControl>

                        <FormControl isInvalid={errors.city}>
                          <FormLabel color="secondary.100">City</FormLabel>
                          <Input
                            {...register("city", { required: "City is required" })}
                            placeholder="New York"
                            variant="light"
                            _placeholder={{ color: "primary.900" }}
                          />
                          <FormErrorMessage>{errors.city && errors.city.message}</FormErrorMessage>
                        </FormControl>

                        <FormControl isInvalid={errors.state}>
                          <FormLabel color="secondary.100">State</FormLabel>
                          <Input
                            {...register("state", { required: "State is required" })}
                            placeholder="NY"
                            variant="light"
                            _placeholder={{ color: "primary.900" }}
                          />
                          <FormErrorMessage>{errors.state && errors.state.message}</FormErrorMessage>
                        </FormControl>

                        <FormControl isInvalid={errors.country}>
                          <FormLabel color="secondary.100">Country</FormLabel>
                          <Controller
                            name="country"
                            control={control}
                            rules={{ required: 'Country is required' }}
                            render={({ field }) => (
                              <CustomSelect
                                value={field.value}
                                onChange={field.onChange}
                                placeholder="Select country"
                                width="100%"
                                variant="light"
                              >
                                {COUNTRIES.map((country) => (
                                  <option key={country.code} value={country.code}>
                                    {country.name}
                                  </option>
                                ))}
                              </CustomSelect>
                            )}
                          />
                          <FormErrorMessage>{errors.country && errors.country.message}</FormErrorMessage>
                        </FormControl>

                        <FormControl isInvalid={errors.postalCode}>
                          <FormLabel color="secondary.100">ZIP Code</FormLabel>
                          <Input
                            {...register('postalCode', { required: 'ZIP code is required' })}
                            placeholder="10001"
                            variant="light"
                            _placeholder={{ color: "primary.900" }}
                          />
                          <FormErrorMessage>{errors.postalCode && errors.postalCode.message}</FormErrorMessage>
                        </FormControl>
                      </SimpleGrid>

                      <FormControl>
                        <FormLabel color="secondary.100">Card Details</FormLabel>
                        <Box
                          p={4}
                          borderWidth="1px"
                          borderRadius="lg"
                          bg="primary.100"
                          borderColor="dark.900"
                          _hover={{ borderColor: "secondary.500" }}
                          _focusWithin={{ borderColor: "secondary.500" }}
                        >
                          <CardElement options={CARD_OPTIONS} />
                        </Box>
                      </FormControl>

                      {/* Save Payment Method Checkbox */}
                      <FormControl>
                        <Checkbox
                          isChecked={savePaymentMethod}
                          onChange={(e) => setSavePaymentMethod(e.target.checked)}
                          colorScheme="orange"
                          size="md"
                        >
                          <Text color="primary.900" fontSize="sm">
                            Save this payment method for future use
                          </Text>
                        </Checkbox>
                      </FormControl>                    </VStack>
                  )}
                </>
              )}
            </VStack>
          </form>
        </ModalBody>

        <ModalFooter borderTop="1px solid" borderColor="dark.900" pt={4}>
          <HStack spacing={3} w="100%">
            <Button variant="outline" borderColor="dark.900" color="primary.500" onClick={onClose} flex={1}>
              Cancel
            </Button>
            <Button
              variant="solid"
              bg="secondary.500"
              color="dark.900"
              _hover={{ bg: "secondary.600" }}
              onClick={handleSubmit(handleAddFunds)}
              isLoading={loading}
              loadingText="Processing..."
              flex={2}
              isDisabled={!depositAmount || depositAmount < Math.max(5, minimumAmount)}
              leftIcon={<FaLock />}
            >
              {selectedPaymentMethod === 'saved' 
                ? `Pay $${(processingFees.totalToPay || 0).toFixed(2)}` 
                : savePaymentMethod 
                  ? `Save & Pay $${(processingFees.totalToPay || 0).toFixed(2)}`
                  : `Pay $${(processingFees.totalToPay || 0).toFixed(2)}`
              }
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddFundsModal;